import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, ClipboardList, User, UserCog } from "lucide-react";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/recruiting/StatusBadge";

export const UniversalSearch = ({ open, setOpen }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [results, setResults] = useState({ jobs: [], members: [], applications: [], recruiters: [] });
  const [query, setQuery] = useState("");
  const timer = useRef(null);

  useEffect(() => {
    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [setOpen]);

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults({ jobs: [], members: [], applications: [], recruiters: [] });
      return;
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const res = await api.get("/search", { params: { q: query } });
        setResults({ recruiters: [], ...res.data });
      } catch {
        // ignore
      }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [query]);

  const isAdmin = user.role === "admin" || user.role === "super_admin";

  const go = (path) => {
    setOpen(false);
    setQuery("");
    navigate(path);
  };

  const appPath = (id) =>
    isAdmin ? `/admin/applications/${id}` : user.role === "recruiter" ? `/recruiter/applications/${id}` : `/member/applications/${id}`;

  const hasResults = results.jobs.length || results.members.length || results.applications.length || (results.recruiters || []).length;

  return (
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
      <CommandInput
        placeholder="Mobile, member name, application ID, job title, hiring brand, recruiter..."
        value={query}
        onValueChange={setQuery}
        data-testid="universal-search-input"
      />
      <CommandList data-testid="universal-search-results">
        {!hasResults && <CommandEmpty>{query.trim().length < 2 ? "Type at least 2 characters to search" : "No results found."}</CommandEmpty>}
        {results.applications.length > 0 && (
          <CommandGroup heading="Applications">
            {results.applications.map((a) => (
              <CommandItem key={a.id} value={a.id} onSelect={() => go(appPath(a.id))}>
                <ClipboardList className="h-4 w-4 mr-2 text-primary" />
                <span className="flex-1 truncate">
                  <span className="font-mono text-xs mr-2">{a.code}</span>
                  {a.member_name} — {a.job_title}
                </span>
                <StatusBadge status={a.status} className="text-[10px]" />
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.jobs.length > 0 && (
          <CommandGroup heading="Jobs">
            {results.jobs.map((j) => (
              <CommandItem key={j.id} value={j.id} onSelect={() => go(isAdmin ? "/admin/jobs" : `/jobs/${j.id}`)}>
                <Briefcase className="h-4 w-4 mr-2 text-primary" />
                <span className="flex-1 truncate">{j.title}</span>
                <span className="text-xs text-muted-foreground">{j.hiring_brand}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.members.length > 0 && (
          <CommandGroup heading="Members">
            {results.members.map((m) => (
              <CommandItem key={m.id} value={m.id} onSelect={() => go(isAdmin ? "/admin/members" : "/recruiter/applications")}>
                <User className="h-4 w-4 mr-2 text-primary" />
                <span className="flex-1">{m.name}</span>
                <span className="text-xs font-mono text-muted-foreground">{m.mobile}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {(results.recruiters || []).length > 0 && (
          <CommandGroup heading="Recruiters">
            {results.recruiters.map((r) => (
              <CommandItem key={r.id} value={r.id} onSelect={() => go("/admin/recruiters")}>
                <UserCog className="h-4 w-4 mr-2 text-primary" />
                <span className="flex-1">{r.name}</span>
                <span className="text-xs font-mono text-muted-foreground">{r.mobile}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};
